// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Ctx } from '../main.ts';
import { binValues, niceMax, type Bin } from '../utils/layout.ts';
import { GROUP_META } from '../parties.ts';
import { esc, escTip, fmtSigned } from '../utils/format.ts';
import { term } from '../glossary.ts';

type Mode = 'margin' | 'swing';

export function renderDistribution(root: HTMLElement, ctx: Ctx): void {
  let mode: Mode = 'margin';

  root.innerHTML = `
    <div class="view-head">
      <h2>Distribution</h2>
      <p class="subtitle">How the 150 seats spread out. Most are safe; the election is decided in the thin tail of ${term('margin', 'marginal')} seats. Click a bar to list the seats in it.</p>
    </div>
    <div class="panel">
      <div class="controls">
        <div class="seg" id="dist-seg">
          <button data-mode="margin" class="active">Margin of victory</button>
          <button data-mode="swing">Swing to Labor</button>
        </div>
      </div>
      <div class="panel-sub" id="dist-sub"></div>
      <div class="chart-wrap"><svg class="chart" id="dist-svg" viewBox="0 0 960 440" preserveAspectRatio="xMidYMid meet" role="img"></svg></div>
      <div id="dist-drill"></div>
    </div>`;

  const svg = root.querySelector('#dist-svg') as SVGSVGElement;
  const sub = root.querySelector('#dist-sub')!;
  const drill = root.querySelector('#dist-drill')!;
  let binnedSeats = ctx.data.seats; // the exact array the current bins index into

  const draw = () => {
    drill.innerHTML = '';
    let seats = ctx.data.seats;
    let values: number[]; let min: number; let max: number; let bins: number;
    if (mode === 'margin') {
      values = seats.map((s) => s.marginPct);
      min = 0; max = Math.ceil(Math.max(...values) / 2) * 2; bins = Math.round(max / 2);
      sub.textContent = 'Two-candidate margin of victory, in 2-point bins. A tall spike near zero would mean a knife-edge parliament; here the mass sits in safe territory with a long marginal tail.';
    } else {
      // Only seats with a comparable 2022 two-party figure carry a swing.
      seats = seats.filter((s) => s.tppSwingToALP != null);
      values = seats.map((s) => s.tppSwingToALP as number);
      const m = Math.ceil(Math.max(...values.map(Math.abs)));
      min = -m; max = m; bins = 2 * m; // 1-point bins
      const excluded = ctx.data.national.noSwingCount;
      sub.textContent = `Two-party swing per seat. Negative (blue) is a swing to the Coalition, positive (red) to Labor. Most seats moved Labor’s way, but a real cluster went the other direction.${excluded ? ` ${excluded} seat${excluded === 1 ? '' : 's'} that were non-classic in 2022 are excluded — they have no comparable prior.` : ''}`;
    }
    binnedSeats = seats;
    const binned = binValues(values, min, max, bins);
    drawHist(svg, binned, min, max, mode, (bin) => showSeats(bin));
  };

  const showSeats = (bin: Bin) => {
    const inBin = bin.idx.map((i) => binnedSeats[i]).sort((a, b) =>
      mode === 'margin' ? a.marginPct - b.marginPct : (a.tppSwingToALP as number) - (b.tppSwingToALP as number));
    const rangeLabel = mode === 'margin'
      ? `margin ${bin.x0.toFixed(0)}–${bin.x1.toFixed(0)}%`
      : `swing ${fmtSigned(bin.x0, 0)} to ${fmtSigned(bin.x1, 0)}`;
    drill.innerHTML = `<div style="margin-top:var(--sp-lg)">
      <div class="panel-sub"><strong>${inBin.length}</strong> seat${inBin.length === 1 ? '' : 's'} · ${esc(rangeLabel)}</div>
      <div class="chip-row">${inBin.map((s) => `<button class="chip" data-seat="${esc(s.name)}" style="border-left:3px solid ${GROUP_META[s.winnerGroup].color}">${esc(s.name)} <span style="color:var(--text-tertiary)">${esc(s.state)}</span></button>`).join('')}</div>
    </div>`;
    drill.querySelectorAll<HTMLElement>('.chip[data-seat]').forEach((c) => c.addEventListener('click', () => ctx.openSeat(c.dataset.seat!)));
  };

  root.querySelectorAll<HTMLButtonElement>('#dist-seg button').forEach((b) => {
    b.addEventListener('click', () => {
      mode = b.dataset.mode as Mode;
      root.querySelectorAll('#dist-seg button').forEach((x) => x.classList.toggle('active', x === b));
      draw();
    });
  });
  draw();
}

function drawHist(svg: SVGSVGElement, bins: Bin[], min: number, max: number, mode: Mode, onClick: (b: Bin) => void): void {
  const W = 960, H = 440, padL = 44, padR = 16, padT = 16, padB = 46;
  const iw = W - padL - padR, ih = H - padT - padB;
  const maxCount = niceMax(Math.max(...bins.map((b) => b.count), 1));
  const bw = iw / bins.length;
  const x = (v: number) => padL + ((v - min) / (max - min)) * iw;
  const y = (c: number) => padT + ih - (c / maxCount) * ih;

  const gridlines: string[] = [];
  const ticks = 5;
  for (let i = 0; i <= ticks; i++) {
    const c = (maxCount / ticks) * i;
    gridlines.push(`<line class="grid-line" x1="${padL}" y1="${y(c)}" x2="${W - padR}" y2="${y(c)}"/><text class="axis-label" x="${padL - 6}" y="${y(c) + 4}" text-anchor="end">${Math.round(c)}</text>`);
  }
  const bars = bins.map((b) => {
    const bx = x(b.x0) + 1.5;
    const bWidth = Math.max(1, bw - 3);
    const by = y(b.count);
    const color = mode === 'swing' ? (b.x0 + b.x1) / 2 >= 0 ? GROUP_META.ALP.color : GROUP_META.COA.color : '#1e3a5f';
    const label = mode === 'margin' ? `${b.x0.toFixed(0)}–${b.x1.toFixed(0)}%` : `${fmtSigned(b.x0, 0)}…${fmtSigned(b.x1, 0)}`;
    return `<rect class="hist-bar" x="${bx}" y="${by}" width="${bWidth}" height="${Math.max(0, padT + ih - by)}" rx="2" fill="${color}" data-x0="${b.x0}" style="cursor:pointer" data-tip="${escTip(`${label}\n${b.count} seat${b.count === 1 ? '' : 's'}`)}"><title>${esc(label)}: ${b.count}</title></rect>`;
  }).join('');

  // x-axis labels (every other bin to avoid crowding)
  const xlabels = bins.map((b, i) => (i % 2 === 0 || bins.length <= 12)
    ? `<text class="axis-label" x="${x(b.x0)}" y="${H - padB + 16}" text-anchor="middle">${mode === 'margin' ? b.x0.toFixed(0) : fmtSigned(b.x0, 0)}</text>`
    : '').join('');
  const axisTitle = mode === 'margin' ? 'Margin of victory (%)' : 'Swing to Labor (points)';

  svg.innerHTML = `${gridlines.join('')}
    <line class="axis-line" x1="${padL}" y1="${padT + ih}" x2="${W - padR}" y2="${padT + ih}"/>
    ${bars}${xlabels}
    <text class="axis-label" x="${padL + iw / 2}" y="${H - 6}" text-anchor="middle" font-weight="600">${axisTitle}</text>`;

  svg.querySelectorAll<SVGRectElement>('.hist-bar').forEach((r, i) => {
    r.addEventListener('click', () => {
      svg.querySelectorAll('.hist-bar').forEach((x2) => x2.setAttribute('stroke', 'none'));
      r.setAttribute('stroke', '#0f1c2e'); r.setAttribute('stroke-width', '2');
      onClick(bins[i]);
    });
  });
}
